import { FC, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useIdentificationStatus } from "@/hooks/identificationStatusQueryOptions";
import { useGetIdentificationURL } from "@/services/api";
import { IdentificationStatusResponseKycStatus } from "@/services/model";
import {
  isIdCheckPageActionable,
  isRetryIdCheckStatus,
} from "@/utils/kycTodoConfig";

import { WarningWithKYCButton } from "./WarningWithKYCButton";

const OPEN_COPY = {
  default: {
    title: "cockpit.idCheck.title",
    info: "cockpit.idCheck.info",
  },
  fromOrder: {
    title: "cockpit.idCheck.titleFromOrder",
    info: "cockpit.idCheck.infoFromOrder",
  },
} as const;

type KYCWarningProps = {
  /** Post-checkout entry: use order-context intro copy for OPEN status. */
  fromOrder?: boolean;
};

export const KYCWarning: FC<KYCWarningProps> = ({ fromOrder = false }) => {
  const { t, i18n } = useTranslation();

  const staleTime = useRef(0);
  const { data: identificationStatus } = useIdentificationStatus(staleTime);
  const kycStatus = identificationStatus?.kycStatus;

  const { data: identificationUrl } = useGetIdentificationURL({
    query: {
      enabled:
        kycStatus === IdentificationStatusResponseKycStatus.OPEN ||
        isRetryIdCheckStatus(kycStatus),
      refetchOnWindowFocus: true,
      staleTime: 5 * 60 * 1000,
      retry: false,
    },
  });

  const identificationUrlWithLanguage = useMemo(() => {
    if (identificationUrl) {
      const url = new URL(identificationUrl);
      url.searchParams.set("lang", i18n?.resolvedLanguage ?? "en");

      return url.toString();
    }
  }, [identificationUrl, i18n.resolvedLanguage]);

  if (!identificationStatus) {
    return null;
  }

  // Non-actionable statuses (onboarding, pending, fraud check, data mismatch,
  // underaged, max attempts) would otherwise fall through to OPEN.
  if (!isIdCheckPageActionable(kycStatus)) {
    return null;
  }

  // Every retryable status shares the same "ID check failed" copy.
  if (isRetryIdCheckStatus(kycStatus)) {
    return (
      <WarningWithKYCButton
        title={t("portal:cockpit.kycSection.idCheckFailed.title")}
        info={t("portal:cockpit.kycSection.idCheckFailed.text")}
        buttonLabel={t("cockpit.idCheck.button")}
        target={identificationUrlWithLanguage}
      />
    );
  }

  const openCopy = OPEN_COPY[fromOrder ? "fromOrder" : "default"];

  return (
    <WarningWithKYCButton
      title={t(openCopy.title)}
      info={t(openCopy.info)}
      warningInfo={t("cockpit.idCheck.internationalCallsWarning")}
      buttonLabel={t("cockpit.idCheck.button")}
      target={identificationUrlWithLanguage}
    />
  );
};
